import {
  bearing as turfBearing,
  distance as turfDistance,
  lineString as turfLineString,
  point as turfPoint,
  pointToLineDistance
} from '@turf/turf';
import { SPUR_MAX_METERS, SPUR_TOLERANCE_METERS, TURNAROUND_DEGREES } from './joinSpurs';
import { ManeuverSign, signForAngle } from './maneuverSign';

/**
 * Turns that happen exactly where two planner segments meet.
 *
 * Every segment is routed between two waypoints, so GraphHopper opens each one with
 * "continue" — it has no idea the previous segment arrived from a different direction,
 * because it never saw it. A corner the planner put a marker on therefore arrives as sign 0
 * and is stripped as a seam artifact along with the genuine noise. The turn is invisible in
 * the instructions but plainly there in the geometry, which is what this reads.
 *
 * The extreme case is an out-and-back turnaround: the halfway point is a 180° reversal that
 * nothing announces at all.
 *
 * Deliberately additive — a seam is only reported when nothing already covers it, so a route
 * whose instructions are complete comes out unchanged.
 *
 * A turnaround is the one seam a nearby routed turn does not cover. A marker a dozen metres up
 * a side path puts the junction's turns on both sides of it, well inside the separation window
 * — but they describe the junction, and the hiker still has to walk up to the marker and turn
 * back. A port of iOS's `SeamManeuverDetector`, rule for rule.
 */

export interface PointWithDistance {
  /** [lng, lat] */
  coordinates: number[];
  distanceMeters: number;
}

export interface Seam {
  distanceFromStartMeters: number;
  sign: number;
}

/**
 * How far either side of the seam to sample the heading. A single vertex pair can be a metre
 * long and its bearing is mostly noise; a stretch either side is the direction a walker
 * actually experiences.
 */
const WINDOW_METERS = 20;

/** A seam this close to a real instruction is that instruction, seen from the geometry. */
const MIN_SEPARATION_METERS = 25;

/** Closer than this, a vertex is the seam itself and has no heading to give. */
const AT_SEAM_METERS = 0.5;

/**
 * @param turnaroundCoverDistances the existing maneuvers that can speak for a turnaround — a
 * turn that already sends the hiker back, or an entry a person wrote or hid. Only these cover
 * a seam where the route turns around.
 */
export function detectSeams(
  points: PointWithDistance[],
  seamDistances: number[],
  existingManeuverDistances: number[],
  turnaroundCoverDistances: number[] = []
): Seam[] {
  if (points.length < 2 || seamDistances.length === 0) return [];

  const seams: Seam[] = [];
  seamDistances.forEach((seam) => {
    const anchorIndex = nearestIndex(points, seam);
    const turnsAround = retracesItself(points, anchorIndex);
    const covering = turnsAround ? turnaroundCoverDistances : existingManeuverDistances;
    const covered = covering.some((d) => Math.abs(d - seam) < MIN_SEPARATION_METERS);
    if (covered) return;

    const headings = headingsAt(points, seam, anchorIndex);
    if (!headings) return;
    const angle = signedTurn(headings[0], headings[1]);

    // A turnaround is a U-turn whatever the window reads. On a stub shorter than the window
    // both headings reach past the junction and measure the turn off the stub instead — a
    // "sharp right" at a marker the hiker has to turn back from.
    if (turnsAround) {
      seams.push({
        distanceFromStartMeters: seam,
        sign: angle >= 0 ? ManeuverSign.U_TURN_RIGHT : ManeuverSign.U_TURN_LEFT
      });
      return;
    }

    const sign = signForAngle(angle);
    if (sign === ManeuverSign.CONTINUE) return;
    seams.push({ distanceFromStartMeters: seam, sign });
  });
  return seams;
}

/** The vertex nearest the seam's distance — the first of them, where a join repeats one. */
function nearestIndex(points: PointWithDistance[], seam: number): number {
  let anchorIndex = 0;
  let best = Infinity;
  points.forEach((p, i) => {
    const gap = Math.abs(p.distanceMeters - seam);
    if (gap < best) {
      best = gap;
      anchorIndex = i;
    }
  });
  return anchorIndex;
}

/**
 * Whether the route leaves the seam straight back along the way it came, for further than any
 * stub.
 *
 * `joinSpurs`'s retrace, run on the whole route and with its limit read the other way: a
 * reversal at the seam itself, then the path in walked backwards and the path out walked
 * forwards while the two lie on top of each other. A reversal no longer than `SPUR_MAX_METERS`
 * is the stub the trim removes; one longer is a turnaround the route really makes. It stops at
 * the first place they part, so a junction the route leaves by another path ends the measure
 * there — the length is the stub's, not the whole out-and-back's.
 */
function retracesItself(points: PointWithDistance[], anchorIndex: number): boolean {
  const tip = points[anchorIndex].coordinates;

  // A join repeats its coordinate, once as an end and once as the next start.
  let i = anchorIndex;
  while (i > 0 && sameLocation(points[i - 1].coordinates, tip)) i -= 1;
  let j = anchorIndex;
  while (j < points.length - 1 && sameLocation(points[j + 1].coordinates, tip)) j += 1;

  const into = findIndex(points, i - 1, -1, (p) => meters(p.coordinates, tip) > AT_SEAM_METERS);
  const outOf = findIndex(points, j + 1, 1, (p) => meters(p.coordinates, tip) > AT_SEAM_METERS);
  if (into === undefined || outOf === undefined) return false;
  const tipTurn = signedTurn(
    turfBearing(turfPoint(points[into].coordinates), turfPoint(tip)),
    turfBearing(turfPoint(tip), turfPoint(points[outOf].coordinates))
  );
  if (Math.abs(tipTurn) < TURNAROUND_DEGREES) return false;

  let length = 0;
  while (i > 0 && j < points.length - 1) {
    const back = points[i - 1].coordinates;
    const ahead = points[j + 1].coordinates;
    let nextI = i;
    let nextJ = j;
    if (meters(back, ahead) <= SPUR_TOLERANCE_METERS) {
      nextI = i - 1;
      nextJ = j + 1;
    } else if (toSegment(back, points[j].coordinates, ahead) <= SPUR_TOLERANCE_METERS) {
      nextI = i - 1;
    } else if (toSegment(ahead, back, points[i].coordinates) <= SPUR_TOLERANCE_METERS) {
      nextJ = j + 1;
    } else {
      break;
    }
    if (nextI !== i) {
      length += meters(points[nextI].coordinates, points[i].coordinates);
      if (length > SPUR_MAX_METERS) return true;
    }
    i = nextI;
    j = nextJ;
  }
  return false;
}

/**
 * Heading into and out of the seam.
 *
 * Anchored on the vertex at the join, then walking outwards for something to measure against.
 * The window is a preference, not a requirement: trail geometry is sparse where it is
 * straight, and a route with 45 m between vertices at its turnaround would otherwise pick the
 * anchor itself, leaving no angle to compute and the turn unreported. A vertex further out
 * than asked for still gives the right heading; the same vertex gives nothing.
 */
function headingsAt(
  points: PointWithDistance[],
  seam: number,
  anchorIndex: number
): [number, number] | undefined {
  const anchor = points[anchorIndex].coordinates;

  const before =
    findIndex(points, anchorIndex - 1, -1, (p) => p.distanceMeters <= seam - WINDOW_METERS && !sameLocation(p.coordinates, anchor)) ??
    findIndex(points, anchorIndex - 1, -1, (p) => !sameLocation(p.coordinates, anchor));

  const after =
    findIndex(points, anchorIndex + 1, 1, (p) => p.distanceMeters >= seam + WINDOW_METERS && !sameLocation(p.coordinates, anchor)) ??
    findIndex(points, anchorIndex + 1, 1, (p) => !sameLocation(p.coordinates, anchor));

  if (before === undefined || after === undefined) return undefined;

  return [
    turfBearing(turfPoint(points[before].coordinates), turfPoint(anchor)),
    turfBearing(turfPoint(anchor), turfPoint(points[after].coordinates))
  ];
}

function findIndex(
  points: PointWithDistance[],
  from: number,
  step: number,
  match: (p: PointWithDistance) => boolean
): number | undefined {
  for (let i = from; i >= 0 && i < points.length; i += step) {
    if (match(points[i])) return i;
  }
  return undefined;
}

/** A segment join repeats its coordinate, once as an end and once as the next start. */
function sameLocation(a: number[], b: number[]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function meters(p: number[], q: number[]): number {
  return turfDistance([p[0], p[1]], [q[0], q[1]], { units: 'meters' });
}

/** Metres from `p` to the segment `start`–`end` — the same measure `joinSpurs` uses. */
function toSegment(p: number[], start: number[], end: number[]): number {
  if (meters(start, end) < 0.01) return meters(p, start);
  return pointToLineDistance(
    turfPoint([p[0], p[1]]),
    turfLineString([
      [start[0], start[1]],
      [end[0], end[1]]
    ]),
    { units: 'meters' }
  );
}

/** Turn angle in (-180, 180]; positive is a right turn, matching `signForAngle`. */
function signedTurn(incomingBearing: number, outgoingBearing: number): number {
  let delta = outgoingBearing - incomingBearing;
  while (delta <= -180) delta += 360;
  while (delta > 180) delta -= 360;
  return delta;
}
